import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "../components/Router";
import { callLink } from "../components/Links.mjs";
import { dateComponents } from "../components/Dates.mjs";
import { sortBy } from "../components/Sorting";
import Layout from "./Layout";
import Button from "react-bootstrap/Button";
import calendarImage from "../img/calendar.svg";
import { parse, format, subDays, addDays } from "date-fns";
import SiteHead from "./SiteHead";
import log from "loglevel";
import HeroBgImage from "../img/hero-bg.png";
import "../sass/calendar.scss";
import { bucketizeEvents } from "components/CalendarUtils";

const CalCard = ({
  event: { exchange, symbol, quarter, year, companyName },
}) => {
  const callPath = callLink(exchange, symbol, year, quarter);
  // log.debug("CalCard:", companyName);
  return (
    <>
      <div className="col-xl-3 col-lg-4 col-sm-6 call-card-spacer">
        <Link to={callPath}>
          <div className="call-card">
            <div className="text">{companyName}</div>

            <div className="info">
              <span className="qno">{quarter}</span>
              <span className="qyear">{year}</span>
            </div>
          </div>
        </Link>
      </div>
    </>
  );
};

const CalRow = ({ time, events }) => {
  // log.debug("CalRow:", time, events);
  return (
    <>
      <div className="card">
        <h2 className="card-header">
          <span className="time">{time}</span>{" "}
          <span className="count">
            {events.length} Call{events.length > 1 && "s"}
          </span>
        </h2>
        <div className="card-body">
          <div className="row">
            {events.map((event, i) => (
              <CalCard key={i} event={event} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};



const getCurrentDay = (hash) => {
  log.trace("hash:", hash);
  if (hash) {
    const dateString = hash.substring(1);
    const decodedDateString = decodeURIComponent(dateString);
    const parsedDate = parse(decodedDateString, "yyyy-MM-dd", new Date());
    log.trace("parsedDate:", parsedDate);
    return parsedDate;
    // return new Date(decodedDateString);
  }
  return new Date();
};

const CalendarLink = ({ date, className, children }) => {
  const { year, month, day } = dateComponents(date);

  const linkUrl = `/calendar/${year}/${month}/${day}`;
  // HACK: Manually call prefetch(), forcing react-static to prefetch the page assets.
  //       This is a hack because it should NOT be necessary, but I'm seeing these links NOT be prefetched,
  //       and I'm not sure why.  Calling prefetch() manually seems to fix the issue, but it is a hack.
  // prefetch(linkUrl);

  return (
    <Link to={linkUrl} className={className}>
      {children}
    </Link>
  );
};

const Calendar = ({
  pageContext: { defaultPath, dateString, earningsCalendar, hasNext },
}) => {
  log.debug("Calendar::render()");
  const currentDay = parse(dateString, "yyyy-MM-dd", new Date());
  const previousDay = subDays(currentDay, 1);
  const { year } = dateComponents(previousDay);
  const hasPrevious = parseInt(year) >= 2020;
  const nextDay = addDays(currentDay, 1);
  // const [ currentDay, setCurrentDay ] = useState(parsedDate);
  const currentDayHeaderString = format(currentDay, "PPPP");
  const dayTimeBuckets = bucketizeEvents(earningsCalendar);
  const pageTitleBase = `EarningsCall · Earnings Calendar`;
  const pageTitleDate = format(currentDay, "MM/dd/yyyy");
  const pageTitle = defaultPath
    ? pageTitleBase
    : `${pageTitleBase} - ${pageTitleDate}`;
  const description = defaultPath
    ? `Check out all the earnings calls scheduled for today.`
    : `Check out all the earnings calls scheduled for ${pageTitleDate}.`;
  log.trace("buckets:", dayTimeBuckets);
  const currentDaysEvents = dayTimeBuckets[currentDay.toLocaleDateString()];
  log.trace("today:", currentDaysEvents);

  const orderedDaysEvents =
    currentDaysEvents &&
    Object.entries(currentDaysEvents)
      .map(([time, { conferenceDate, events }]) => {
        // log.info("entry:", conferenceDate, time, events);
        return {
          conferenceDate: conferenceDate,
          time: time,
          events: events,
        };
      })
      .sort(sortBy("conferenceDate", true));

  return (
    <>
      <SiteHead title={pageTitle} description={description} />
      <Helmet>
        {/* Ensure the HeroBgImage is preloaded */}
        <link rel="preload" as="image" href={HeroBgImage} />
      </Helmet>
      <section className="common-hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="title">Earnings Calendar</h1>
          </div>
        </div>
      </section>
      <section className="calendar-section">
        <div className="container">
          <div className="navigation">
            <CalendarLink date={previousDay} className="order-1">
              <Button variant="dark" disabled={!hasPrevious}>
                Previous
              </Button>
            </CalendarLink>
            <div className="date-time order-md-2 order-3">
              <span className="icon">
                <img src={calendarImage} alt="icon" />
              </span>

              <span className="text">{currentDayHeaderString}</span>
            </div>
            <CalendarLink date={nextDay} className="order-md-3 order-2">
              <Button variant="dark" disabled={!hasNext}>
                Next
              </Button>
            </CalendarLink>
          </div>
          {currentDaysEvents ? (
            <>
              {orderedDaysEvents.map(({ time, events }) => (
                <CalRow key={time} time={time} events={events} />
              ))}
            </>
          ) : (
            <>
              <p>No events found.</p>
            </>
          )}
        </div>
      </section>
    </>
  );
};

const CalendarExport = (props) => (
  <Layout {...props}>
    <Calendar {...props} />
  </Layout>
);

export default CalendarExport;
